/**
 * As informações aos compositores (E153.F3, ADR-0104 §2.4 e §2.6): o que cada saída pode consumir
 * agora, com o regime de entrega e a assinatura; a decisão da avaliação; as versões que o resultado
 * carrega; e a evidência em números. O servidor diz o que está disponível; a tela só mostra.
 */
import { Badge, Blueprint } from '@/components/ui';
import {
  codigoCurto,
  fatosPorClasse,
  leituraEscolhida,
  type ResultadoAuditadoData,
  type ResultadoDoCasoData,
} from '@/lib/resultadoAuditado';

import { preencher, simOuNao, t, textoDoMotivoDoConsumo, tf } from './textos';

function Saidas({ dados }: { dados: ResultadoDoCasoData }): JSX.Element {
  const c = t.compositores;
  return (
    <table className="w-full text-body-sm" data-testid="resultado-kinds">
      <thead>
        <tr className="text-left text-caption text-fg-muted">
          <th className="py-1 font-medium">{c.colunas.saida}</th>
          <th className="py-1 font-medium">{c.colunas.regime}</th>
          <th className="py-1 font-medium">{c.colunas.assinatura}</th>
          <th className="py-1 font-medium">{c.colunas.disponivel}</th>
        </tr>
      </thead>
      <tbody>
        {dados.kinds.map((k) => (
          <tr
            key={k.kind}
            className="border-t border-border"
            data-testid="resultado-kind"
            data-kind={k.kind}
            data-disponivel={k.disponivel}
          >
            <td className="py-1 text-fg">{t.kind[k.kind]}</td>
            <td className="py-1">
              <Badge variant={k.regime === 'reservado' ? 'warning' : 'neutral'}>
                {t.regime[k.regime]}
              </Badge>
            </td>
            <td className="py-1 text-fg">{simOuNao(k.assinatura_aplicavel)}</td>
            <td className="py-1 text-fg">
              {k.disponivel
                ? c.disponivel
                : `${c.indisponivel}: ${textoDoMotivoDoConsumo(k.motivo)}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <p className="text-body-sm">
      <span className="text-fg-muted">{rotulo}:</span> <span className="text-fg">{valor}</span>
    </p>
  );
}

/** E180.F2 (GUS-254 item 6): "definida pelo perito" só com revisão registrada em vigor. */
function autoria(d: ResultadoAuditadoData['decisao']): string {
  const c = t.compositores;
  if (d.juizo_pendente) return c.juizoPendente;
  return d.revisoes_em_vigor.length > 0 ? c.juizoDefinido : c.juizoDoSistema;
}

function Decisao({ r }: { r: ResultadoAuditadoData }): JSX.Element {
  const c = t.compositores;
  const d = r.decisao;
  const leitura = leituraEscolhida(r);
  return (
    <div
      className="space-y-sc-1"
      data-testid="resultado-decisao"
      data-juizo-pendente={d.juizo_pendente}
    >
      <h3 className="text-body-md font-semibold text-fg">{c.decisao}</h3>
      <Linha rotulo={c.conclusao} valor={tf.conclusao[d.conclusao]} />
      <Linha
        rotulo={c.proposta}
        valor={d.proposta === null ? c.semProposta : tf.conclusao[d.proposta]}
      />
      {d.motivos_da_pendencia.length > 0 ? (
        <p className="text-body-sm text-fg-muted">
          {d.motivos_da_pendencia.map((m) => tf.motivoDaPendencia[m]).join('; ')}
        </p>
      ) : null}
      <p className="text-body-sm text-fg" data-testid="resultado-autoria">
        {autoria(d)}
      </p>
      <Linha
        rotulo={c.leituraEscolhida}
        valor={leitura === undefined ? c.semLeitura : leitura.titulo}
      />
      {d.razao_da_escolha !== null ? <Linha rotulo={c.razao} valor={d.razao_da_escolha} /> : null}
    </div>
  );
}

function Versoes({ r }: { r: ResultadoAuditadoData }): JSX.Element {
  const c = t.compositores;
  const v = r.versoes;
  return (
    <div className="space-y-sc-1" data-testid="resultado-versoes">
      <h3 className="text-body-md font-semibold text-fg">{c.versoes}</h3>
      <Linha
        rotulo={c.catalogo}
        valor={`${v.catalogo_versao} (${codigoCurto(v.catalogo_sha256)})`}
      />
      <Linha rotulo={c.criterioDoGrau} valor={v.criterio_do_grau} />
      <Linha rotulo={c.veracidade} valor={v.veracidade} />
      <Linha rotulo={c.interpretacao} valor={v.interpretacao} />
      <Linha rotulo={c.reguaDasEtapas} valor={String(v.regra_das_etapas)} />
    </div>
  );
}

function Evidencia({ r }: { r: ResultadoAuditadoData }): JSX.Element {
  const c = t.compositores;
  const e = r.evidencia;
  const classes = fatosPorClasse(e);
  const porClasse = (Object.keys(classes) as (keyof typeof classes)[])
    .map((k) => `${t.classe[k]} ${classes[k]}`)
    .join(' · ');
  return (
    <div className="space-y-sc-1" data-testid="resultado-evidencia">
      <h3 className="text-body-md font-semibold text-fg">{c.evidencia}</h3>
      <p className="text-body-sm text-fg">
        {preencher(c.fontes, { n: e.fontes.length })} · {preencher(c.fatos, { n: e.fatos.length })}{' '}
        ({porClasse}) · {preencher(c.naoExtraidos, { n: e.nao_extraidos.length })} ·{' '}
        {preencher(c.inferencias, { n: e.inferencias.length })} ·{' '}
        {preencher(c.imagens, { n: e.imagens.length })}
      </p>
    </div>
  );
}

export function InformacoesAosCompositores({ dados }: { dados: ResultadoDoCasoData }): JSX.Element {
  return (
    <Blueprint
      as="section"
      className="space-y-sc-4 p-sc-6"
      data-testid="resultado-compositores"
      aria-labelledby="resultado-compositores-titulo"
    >
      <div>
        <h2 id="resultado-compositores-titulo" className="text-body-lg text-fg">
          {t.compositores.titulo}
        </h2>
        <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.compositores.subtitulo}</p>
      </div>
      <Saidas dados={dados} />
      {dados.resultado !== null ? (
        <div className="grid grid-cols-1 gap-sc-4 md:grid-cols-2">
          <Decisao r={dados.resultado} />
          <Versoes r={dados.resultado} />
          <Evidencia r={dados.resultado} />
        </div>
      ) : null}
    </Blueprint>
  );
}
